import { Typography } from '@material-ui/core';
import { FormikCheckboxField } from '@superdispatch/forms';
import { DrawerActions, Inline, Stack } from '@superdispatch/ui';
import { useMemo, useState } from 'react';
import { useAppFormik } from 'shared/form/AppFormik';
import { FormikDrawer, FormikDrawerContent } from 'shared/form/FormikDrawer';
import {
  getApplicableBolTemplates,
  useCarrierSettings,
} from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import { z } from 'zod';
import { FormikContactEmailField } from '../../contacts/core/FormikContactEmailField';
import { loadBOLSendSchema, zodBolTemplate } from '../data/LoadActionsDTO';
import { BolTemplateRadioGroupField } from './BolTemplateRadioGroupField';
import { LoadsPageType, trackLoadsEvent } from '../data/LoadsAnalytics';
import { useLoadDetails, useLoadsAPI, useLoadsCache } from '../data/LoadsAPI';
import { LoadBOLPreviewDialog } from './LoadBOLPreviewDialog';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';
import { useSubhaulLoad } from '../view/useSubhaulLoad';
import { Box } from '@superdispatch/ui-lab';

const zodString = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

// `safeExtend` because `loadBOLSendSchema` carries the email format check.
const loadBOLSendFormSchema = loadBOLSendSchema.safeExtend({ guid: zodString });

interface LoadSendBOLDrawerContentProps {
  loadGUID?: string;
  onClose: () => void;
  pageType: LoadsPageType;
  onSubmitSuccess: () => void;
}

function LoadSendBOLDrawerContent({
  onClose,
  loadGUID,
  pageType,
  onSubmitSuccess,
}: LoadSendBOLDrawerContentProps) {
  const { data: settings } = useCarrierSettings();
  const isCleanBolEnabled = useFlag('ctms_clean_bol');
  const { sendBOL, sendSubhaulerBOL } = useLoadsAPI();
  const { isSubhauled } = useSubhaulLoad();
  const { data, isLoading: isLoadingDetails } = useLoadDetails(loadGUID, {
    isSubhauled,
  });
  const { invalidateLoads } = useLoadsCache();
  const [modalState, setModalState] = useState<'preview'>();
  const applicableBolTemplates = useMemo(
    () =>
      getApplicableBolTemplates(settings?.bol_templates, data?.inspection_type),
    [settings?.bol_templates, data?.inspection_type],
  );
  const shouldShowTemplateField = useMemo(() => {
    if (isCleanBolEnabled) {
      return applicableBolTemplates.length > 1;
    }

    return data?.inspection_type !== 'aiag';
  }, [isCleanBolEnabled, applicableBolTemplates, data?.inspection_type]);
  const defaultTemplate = useMemo(() => {
    if (isCleanBolEnabled && applicableBolTemplates.length === 1) {
      return applicableBolTemplates[0]?.key ?? 'default';
    }

    if (isCleanBolEnabled && applicableBolTemplates.length > 1) {
      return (
        applicableBolTemplates.find(({ key }) => key === 'default')?.key ??
        applicableBolTemplates[0]?.key ??
        'default'
      );
    }

    return 'default';
  }, [isCleanBolEnabled, applicableBolTemplates]);
  const validationSchema = useMemo(() => {
    if (!isCleanBolEnabled) {
      return loadBOLSendFormSchema;
    }

    const templateKeys = applicableBolTemplates.map(({ key }) => key);

    if (templateKeys.length > 1) {
      return loadBOLSendFormSchema.safeExtend({
        template: zodBolTemplate(templateKeys),
      });
    }

    return loadBOLSendFormSchema.safeExtend({ template: zodString });
  }, [isCleanBolEnabled, applicableBolTemplates]);
  const initialValues = useMemo<Partial<z.infer<typeof loadBOLSendFormSchema>>>(
    () => ({
      guid: loadGUID,
      use_broker_info: data?.is_created_by_broker,
      email: data?.customer.venue.contacts[0]?.email,
      template: defaultTemplate,
    }),
    [data, loadGUID, defaultTemplate],
  );
  const formik = useAppFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit({ guid, ...values }) {
      return isSubhauled
        ? sendSubhaulerBOL(guid, values)
        : sendBOL(guid, values);
    },
    onSubmitSuccess() {
      invalidateLoads();
      onSubmitSuccess();
      trackLoadsEvent({ name: 'CTMS: BOL Sent', page: pageType });
    },
    getSuccessMessage({ user_message }) {
      return user_message || 'BOL Sent';
    },
  });

  const { values, isSubmitting } = formik;
  const isLoading = !data || isSubmitting;

  return (
    <>
      <LoadBOLPreviewDialog
        params={values}
        loadGUID={loadGUID}
        open={modalState === 'preview'}
        onClose={() => {
          setModalState(undefined);
        }}
      />

      <FormikDrawerContent
        formik={formik}
        title="Send BOL"
        onClose={onClose}
        disableAutoClose={!!isSubhauled}
        actions={
          <DrawerActions>
            <Inline>
              <PermissionButton
                name="loads"
                permission="can_send_bol"
                type="submit"
                disabled={isLoading}
                pending={isSubmitting}
              >
                Send BOL
              </PermissionButton>

              <PermissionButton
                name="loads"
                permission="can_view_bol"
                variant="neutral"
                disabled={isLoading}
                onClick={() => {
                  trackLoadsEvent({
                    name: 'CTMS: Previewed BOL In Send BOL',
                    page: pageType,
                  });
                  setModalState('preview');
                }}
              >
                Preview BOL
              </PermissionButton>
            </Inline>
          </DrawerActions>
        }
      >
        <Stack space="small">
          <FormikContactEmailField
            name="email"
            disabled={isLoading}
            TextFieldProps={{
              label: 'Email',
              fullWidth: true,
              placeholder: 'example@mail.com',
            }}
          />

          {shouldShowTemplateField && (
            <Box maxWidth="360px">
              <Stack space="xsmall">
                <Typography variant="h5" color="textSecondary">
                  BOL Template
                </Typography>
                <BolTemplateRadioGroupField
                  name="template"
                  isCleanBolEnabled={isCleanBolEnabled}
                  applicableBolTemplates={applicableBolTemplates}
                  disabled={isLoading}
                />
              </Stack>
            </Box>
          )}

          {settings?.can_show_shipper_info_in_bol &&
            !isSubhauled &&
            !data?.is_received_via_subhaul &&
            !isLoadingDetails && (
              <FormikCheckboxField
                disabled={isLoading}
                name="use_broker_info"
                label="Show Shipper/Customer info in BOL header"
              />
            )}
        </Stack>
      </FormikDrawerContent>
    </>
  );
}

export function LoadSendBOLDrawer({
  loadGUID,
  onClose,
  ...props
}: LoadSendBOLDrawerContentProps) {
  return (
    <FormikDrawer onClose={onClose} open={!!loadGUID}>
      <LoadSendBOLDrawerContent
        {...props}
        onClose={onClose}
        loadGUID={loadGUID}
      />
    </FormikDrawer>
  );
}
